import { ref, computed, readonly, type DeepReadonly, type Ref } from "vue";
import * as authApi from "@/api/auth";
import { tokenStorage, authEvents } from "@/api/client";
import { handleError } from "@/services/errorHandler";
import type {
  OtpPurpose,
  RegisterPayload,
  ResetPasswordPayload,
  User,
} from "@/types";

const user = ref<User | null>(null);
const loading = ref(false);
const error = ref("");
const initialized = ref(false);

const isAuthenticated = computed(
  () => !!user.value || !!tokenStorage.getAccess()
);

let listenerAttached = false;
function attachUnauthorizedListener(): void {
  if (listenerAttached) return;
  listenerAttached = true;
  authEvents.addEventListener("unauthorized", () => {
    user.value = null;
  });
}

export function useAuth() {
  attachUnauthorizedListener();

  async function login(
    phone_number: string,
    password: string
  ): Promise<boolean> {
    loading.value = true;
    error.value = "";
    try {
      await authApi.login(phone_number, password);
      await fetchCurrentUser();
      return true;
    } catch (e) {
      error.value = handleError(e, "useAuth.login");
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function sendOtp(
    phone_number: string,
    purpose: OtpPurpose
  ): Promise<boolean> {
    loading.value = true;
    error.value = "";
    try {
      await authApi.sendOtp(phone_number, purpose);
      return true;
    } catch (e) {
      error.value = handleError(e, "useAuth.sendOtp");
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function verifyOtp(
    phone_number: string,
    code: string,
    purpose: OtpPurpose
  ): Promise<string | null> {
    loading.value = true;
    error.value = "";
    try {
      return await authApi.verifyOtp(phone_number, code, purpose);
    } catch (e) {
      error.value = handleError(e, "useAuth.verifyOtp");
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function register(payload: RegisterPayload): Promise<boolean> {
    loading.value = true;
    error.value = "";
    try {
      await authApi.register(payload);
      await fetchCurrentUser();
      return true;
    } catch (e) {
      error.value = handleError(e, "useAuth.register");
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function resetPassword(
    payload: ResetPasswordPayload
  ): Promise<boolean> {
    loading.value = true;
    error.value = "";
    try {
      await authApi.resetPassword(payload);
      return true;
    } catch (e) {
      error.value = handleError(e, "useAuth.resetPassword");
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function fetchCurrentUser(): Promise<User | null> {
    if (!tokenStorage.getAccess()) {
      user.value = null;
      return null;
    }
    try {
      user.value = await authApi.getCurrentUser();
      return user.value;
    } catch {
      user.value = null;
      return null;
    }
  }

  async function refresh(): Promise<boolean> {
    try {
      await authApi.refresh();
      return true;
    } catch {
      logout();
      return false;
    }
  }

  function logout(): void {
    authApi.logout();
    user.value = null;
  }

  async function init(): Promise<void> {
    if (initialized.value) return;
    initialized.value = true;
    if (tokenStorage.getAccess()) {
      await fetchCurrentUser();
    }
  }

  return {
    user: readonly(user) as DeepReadonly<Ref<User | null>>,
    loading: readonly(loading),
    error: readonly(error),
    isAuthenticated,
    login,
    sendOtp,
    verifyOtp,
    register,
    resetPassword,
    logout,
    refresh,
    fetchCurrentUser,
    init,
  };
}
